import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { withSnackbar } from 'notistack';

import PageContainer from 'ui/PageContainer';
import PageLoader from 'ui/PageLoader';
import { findPolicyType } from './Types';
import { loadPolices, updatePolicy } from './services';
import { withCompanyContext } from '../CompanyContext';
import { loadCompany } from '../../company/company/services';
import { loadUsers } from '../Services';

const policiesStyles = theme => ({
    wrapper: {
        marginBottom: theme.spacing(4),
    },
    policies: {
        paddingTop: theme.spacing(2),
    },
});

const Policies = ({ company, editable, enqueueSnackbar }) => {
    const [loading, setLoading] = useState(true);
    const [policies, setPolicies] = useState([]);
    const [storageRegion, setStorageRegion] = useState([]);
    const [organization, setOrganization] = useState({});
    const [pb6Enabled, setPb6Enabled] = useState({});

    const showPolicyUpdateToast = (policyName, enabled) => {
        const action = enabled ? 'enabled' : 'disabled';
        const message = `${policyName} ${action} successfully`;
        enqueueSnackbar(message, {
            variant: 'success',
            autoHideDuration: 4000,
        });
    };

    const showErrorToast = policyName => {
        enqueueSnackbar(`Failed to update ${policyName}. Please try again.`, {
            variant: 'error',
            autoHideDuration: 6000,
        });
    };

    const onChange = data => {
        const policy = policies.find(p => p.uuid === data.uuid);
        const policyName = policy?.policy?.name || 'Policy';

        return updatePolicy(company.id, data)
            .then(updatedPolicy => {
                setPolicies(updatePolicyState(updatedPolicy));
                showPolicyUpdateToast(policyName, data.enabled);
            })
            .catch(error => {
                showErrorToast(policyName);
            });
    };
    const fetchPolices = () => {
        loadCompany(company.id).then(company => {
            setOrganization(company);
        });
        loadPolices(company.id).then(({ policies, storageRegion }) => {
            setStorageRegion(storageRegion);
            setPolicies(policies);
            setLoading(false);
        });
        loadUsers(company.id).then(({ users, stats }) => {
            const hasPb6AuthEnabled = users
                .filter(user => user.role === 'Administrator')
                .some(admin => admin.pb6_authentication_enabled === true);
            setPb6Enabled(hasPb6AuthEnabled);
        });
    };

    useEffect(() => fetchPolices(), []);

    const updatePolicyState = policy =>
        policies.map(p => (p.uuid === policy.uuid ? policy : p));

    return (
        <PoliciesView
            loading={loading}
            policies={policies}
            storageRegion={storageRegion}
            editable={editable}
            subscriptionLevel={organization.subscription_level}
            onChange={onChange}
            pb6Enabled={pb6Enabled}
        />
    );
};

const POLICIES_GROUPS = {
    standard: group => group === 'Recommended' || group === 'Optional',
    advanced: group => group === 'Advanced',
};

const groupPolicies = policies =>
    Object.keys(POLICIES_GROUPS).reduce((groups, name) => {
        const groupfilter = POLICIES_GROUPS[name];
        groups[name] = policies.filter(policy =>
            groupfilter(policy.policy.group)
        );
        return groups;
    }, {});

const PoliciesView = withStyles(policiesStyles)(props => {
    const {
        classes,
        loading,
        policies = [],
        storageRegion,
        editable,
        onChange,
        subscriptionLevel,
        pb6Enabled,
    } = props;

    if (loading) {
        return <PageLoader />;
    }

    const { standard, advanced } = groupPolicies(policies);

    const renderPolicies = (policies, storageRegion, allowed = true) => {
        return policies.map((policy, index) => {
            return findPolicyType(policy.policy.lookup, {
                index,
                policy,
                storageRegion,
                editable: editable && allowed,
                onChange,
                pb6Enabled,
            });
        });
    };

    const allowedAdvanced = subscriptionLevel === 'advanced';

    return (
        <PageContainer>
            <div className={classes.wrapper}>
                <Typography variant="h6">Standard</Typography>
                <div className={classes.policies}>
                    {renderPolicies(standard, storageRegion)}
                </div>
            </div>
            <div className={classes.wrapper}>
                <Typography variant="h6">Advanced</Typography>
                <Typography variant="subtitle1">
                    Available to all companies on an Advanced subscription
                </Typography>
                <div className={classes.policies}>
                    {renderPolicies(advanced, storageRegion, allowedAdvanced)}
                </div>
            </div>
        </PageContainer>
    );
});

export default withSnackbar(withCompanyContext(Policies));
